import { CSSProperties, ReactNode, useEffect, useMemo, useState } from 'react';
import css from './index.module.scss';
import { Chip, IconNode, ToolTip, Typography } from '@components/base';
import { EstimateDatesModal, NestedChip } from '@components/common';
import Images from '@assets/images';
import { OrderItem, OrderInfo } from '@helpers/types/order';

import { checkScreenType, getCommaSeperatedCurrencyAmount, getFormattedDate } from '@helpers/utils';
import StepProgressBar from '@components/common/progress-bar/progress-bar-step';
import { MultiplePlaceholder } from '@components/common';
import moment from 'moment';
import { updateTaskEstimate } from '@services/order.service';
import notify from '@helpers/toastify-helper';
import { useNavigate } from 'react-router-dom';
import { CURRENCY, SCREEN_TYPE } from '@helpers/constants';

interface OrderCardProps {
  customerName: string;
  items: OrderItem[];
  paymentTerm: string;
  placeOfDelivery: string;
  createdAt: Date;
  totalAmount: string;
  amountDue: string;
  currentState: string;
  cancelStatus?: any;
  orderInfo: OrderInfo;
  pendingTasks: string[];
  handleTabChange: (newValue: number, subtab?: number) => void;
  setOrderOverviewSectionValue?: React.Dispatch<React.SetStateAction<number>>;
}

const InternalOrderCard = (props: OrderCardProps) => {
  const {
    customerName,
    placeOfDelivery,
    createdAt,
    totalAmount,
    amountDue,
    currentState,
    orderInfo,
    paymentTerm,
    pendingTasks,
    handleTabChange,
    setOrderOverviewSectionValue,
    items
  } = props;

  const { currency } = orderInfo;
  const navigate = useNavigate();
  const [estimateModal, setEstimateModal] = useState(false);
  const [progressBarState, setProgressBarState] = useState<any>({
    labelList: [],
    stepLabels: [],
    isCurrent: 0,
    sezSuppliers: [],
    sezProducts: []
  });
  const { labelList, stepLabels, isCurrent, sezProducts, sezSuppliers } = progressBarState;

  const supplierName = useMemo(() => {
    const names = items
      .filter((data) => data?.supplier_name !== undefined)
      .map((data) => data.supplier_name);
    return <MultiplePlaceholder names={names} />;
  }, [props.items]);

  const productName = useMemo(() => {
    const names = items.map((data) => data.product_name);
    if (checkScreenType() === SCREEN_TYPE.mobile.label) {
      return names?.length > 1 ? names.join(', ') : names[0];
    } else {
      return <MultiplePlaceholder names={names} />;
    }
  }, [props.items]);

  const formattedCreatedAt = useMemo(() => {
    return getFormattedDate(createdAt);
  }, [createdAt]);

  const Currency = CURRENCY.find((item) => item.label === currency);
  const formattedTotalAmount = getCommaSeperatedCurrencyAmount(currency, totalAmount);
  const formattedAmountDue = getCommaSeperatedCurrencyAmount(currency, amountDue);

  const isSezOrEou = useMemo(() => {
    if (!items || !items.length) return false;
    const suppliers: any = [];
    const products: any = [];

    items.map((item) => {
      if (item.is_sez_or_eou == true) {
        if (item.supplier_name && !suppliers.includes(item.supplier_name)) {
          suppliers.push(item.supplier_name);
        }
        if (item.product_name && !products.includes(item.product_name)) {
          products.push(item.product_name);
        }
      }
    });

    setProgressBarState((prevState: any) => ({
      ...prevState,
      sezSuppliers: suppliers,
      sezProducts: products
    }));

    return suppliers.length > 0 || products.length > 0;
  }, [items]);

  const cancelStatus = useMemo(() => {
    return {
      is_cancelled: orderInfo?.is_cancelled,
      cancel_remarks: orderInfo?.cancel_remarks,
      last_updated_by: orderInfo?.last_updated_by,
      updated_at: getFormattedDate(orderInfo?.updated_at)
    };
  }, [orderInfo]);

  const onEstimateFormSubmit = async (data: any) => {
    if (data?.estimate_data?.length) {
      const body = data?.estimate_data.map((item: any) => {
        return {
          state_type: item.state_type,
          estimated_date: item.estimated_date
            ? moment(item.estimated_date).format('YYYY-MM-DD')
            : null,
          id: item.id
        };
      });

      const response = await updateTaskEstimate(orderInfo?.order_id ?? '', body);
      if (response.success) {
        notify({
          message: 'Task Estimates added successfully'
        });
        setEstimateModal(false);
      } else {
        notify({ message: response.error ?? 'Something went wrong!', severity: 'error' });
      }
    }
  };

  useEffect(() => {
    if (orderInfo?.customer_state_activity_log) {
      const labels = orderInfo?.customer_state_activity_log.map((item: any) => {
        return {
          label: item.state_name,
          date:
            getFormattedDate(
              item.actual_date ? item.actual_date : item.estimated_date ? item.estimated_date : ''
            ) ?? '',
          logs: item.logs?.map((log: any) => {
            return {
              label: log.log,
              date: getFormattedDate(log.created_at ?? '') ?? ''
            };
          }),
          is_current_state: item.is_current_state
        };
      });

      const isCurrentState = orderInfo?.is_order_completed
        ? Number.MAX_VALUE
        : orderInfo?.customer_state_activity_log.findIndex(
            (item: any) => item.is_current_state === true
          );

      setProgressBarState((prevState: any) => ({
        ...prevState,
        labelList: labels,
        stepLabels: stepLabels,
        isCurrent: isCurrentState
      }));
    }
  }, [orderInfo]);

  const openCustomer = () => {
    window.open(`/customer/${orderInfo.customer?.customer_id}`, '_blank');
  };

  /*
  This function navigates from any tab to the 'Order Overview' tab and scrolls to a specified reference. 
  To use this function, provide the reference of the target component.
*/
  const handleOrderOverviewRefClick = (value: number) => {
    setOrderOverviewSectionValue && setOrderOverviewSectionValue(value);
    handleTabChange(0);
  };

  const handlePendingTaskClick = () => {
    handleTabChange(5, 1);
  };

  return (
    <>
      <div className={css.orderCardContainer}>
        {cancelStatus.is_cancelled ? (
          <div className={css.cancelWrapper}>
            <div className={css.columnWrapper}>
              <Typography variant="p" className={css.cancelOrderTitle}>
                This order has been cancelled by {cancelStatus.last_updated_by ?? 'Unknown User'} on{' '}
                {cancelStatus.updated_at}
              </Typography>
              <Typography variant="p">
                {cancelStatus.cancel_remarks ?? 'No Cancel Remarks'}
              </Typography>
            </div>
          </div>
        ) : (
          <></>
        )}
        <div className={css.orderCardFirstWrapper}>
          <UserCard
            src={Images.personGrey}
            primaryTitle="Customer"
            onClick={openCustomer}
            secondaryTitle={customerName}
          />
          <UserCard
            src={Images.personGrey}
            primaryTitle={
              <div
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}
              >
                Supplier
                {isSezOrEou && (
                  <ToolTip
                    title={`Shipment to be delivered directly to CFS. The following suppliers are in a SEZ: ${sezSuppliers?.join(
                      ', '
                    )}`}
                    placement="top"
                  >
                    <div>
                      <Chip variant="filled-blue" label={'Sez / EOU'} />
                    </div>
                  </ToolTip>
                )}
              </div>
            }
            secondaryTitle={supplierName}
          />
          <UserCard
            src={Images.productGrey}
            primaryTitle={
              <div
                style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}
              >
                Product
                {isSezOrEou && (
                  <ToolTip
                    title={`Shipment to be delivered directly to CFS. The following products are manufactured in a SEZ: ${sezProducts?.join(
                      ', '
                    )}`}
                    placement="top"
                  >
                    <div>
                      <Chip variant="filled-blue" label={'Sez / EOU'} />
                    </div>
                  </ToolTip>
                )}
              </div>
            }
            secondaryTitle={productName}
            style={{ borderRight: '1px solid var(--color-grey-1)' }}
          />
          <div className={css.userCardContainer}>
            <div className={css.statusContainer}>
              <Typography variant="label">Pending Tasks</Typography>
              {/* <NestedChip primaryLabel={'Post order confirmation'} secondaryLabel={'PO Generated'} /> */}
              <Typography className={css.statusText}>
                <MultiplePlaceholder names={pendingTasks} onClick={handlePendingTaskClick} />
              </Typography>
            </div>
          </div>
        </div>
        <div className={css.orderCardFirstWrapper}>
          <UserCard
            primaryTitle="Payment Terms"
            secondaryTitle={paymentTerm}
            showPrimaryIcon={false}
            showInfoIcon={false}
          />
          <UserCard
            primaryTitle="Due Payment/Total Payment"
            secondaryTitle={`${formattedAmountDue}/${formattedTotalAmount}`}
            showPrimaryIcon={false}
            showInfoIcon={false}
            onClick={() => handleOrderOverviewRefClick(6)}
          />
          <UserCard
            primaryTitle="Place of Delivery"
            secondaryTitle={placeOfDelivery?.length ? placeOfDelivery : '-'}
            showPrimaryIcon={false}
            showInfoIcon={false}
          />
          <UserCard
            primaryTitle="Created On"
            secondaryTitle={formattedCreatedAt}
            showPrimaryIcon={false}
            showInfoIcon={false}
          />
        </div>
        <div className={css.progressBarCard}>
          {orderInfo?.estimate_date_edit ? (
            <IconNode
              src={Images.editDark}
              alt="edit"
              className={css.editIcon}
              onClick={() => setEstimateModal(true)}
            />
          ) : (
            <></>
          )}
          <div className={css.rowWrapper}>
            <StepProgressBar label={labelList} currentStep={isCurrent} />
          </div>
        </div>
      </div>
      {estimateModal && (
        <EstimateDatesModal
          open={estimateModal}
          onClose={() => setEstimateModal(false)}
          taskData={{ task_estimates: orderInfo?.task_estimates }}
          onFormSubmit={onEstimateFormSubmit}
        />
      )}
    </>
  );
};

interface UserCardProps {
  src?: string;
  primaryTitle: ReactNode;
  secondaryTitle: ReactNode;
  showPrimaryIcon?: boolean;
  showInfoIcon?: boolean;
  style?: CSSProperties;
  onClick?: () => void;
}
const UserCard = (props: UserCardProps) => {
  const {
    src,
    primaryTitle,
    secondaryTitle,
    style,
    showPrimaryIcon = true,
    showInfoIcon = false,
    onClick
  } = props;
  return (
    <div className={css.userCardContainer} style={style}>
      {showPrimaryIcon && (
        <IconNode src={src} alt={`${primaryTitle} icon`} className={css.profileIcon} />
      )}
      <div className={css.userCardDetailColumn}>
        <div className={`${css.titleWrapper}`}>
          <Typography variant="span" className={css.primaryTitleLabel}>
            {primaryTitle}
          </Typography>
          {showInfoIcon && <IconNode src={Images.infoGrey} alt="info icon" />}
        </div>
        <Typography
          className={`${onClick ? css.hoverText : null}`}
          onClick={() => (onClick ? onClick() : undefined)}
          variant="body"
        >
          {secondaryTitle}
        </Typography>
      </div>
    </div>
  );
};
export default InternalOrderCard;
